import { useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileText,
  Flame,
  HelpCircle,
  Home,
  Layers,
  Lock,
  MessageCircle,
  Network,
  Play,
  Podcast,
  Presentation,
  Settings,
  Sparkles,
  Video,
  type LucideIcon,
} from "lucide-react";
import { SAAS_THEMES, EDITORIAL_THEMES, isEditorialTheme, tokenToCssVars, type StyleTheme, type ThemeMode } from "@/lib/style-themes";
import { cn } from "@/lib/cn";

type View = "dashboard" | "chapters" | "studymodes";

// ===========================================================================
// Sample data -- same numbers across every theme so only the design differs.
// ===========================================================================
const SAMPLE = {
  user: { name: "Usmaan", firstName: "Usmaan" },
  date: { greeting: "Good morning", display: "Mon · 13 May" },
  streak: 5,
  thisWeekMinutes: 204,
  thisWeekSparkline: [
    { day: "M", min: 22 }, { day: "T", min: 38 }, { day: "W", min: 18 },
    { day: "T", min: 52 }, { day: "F", min: 28 }, { day: "S", min: 35 },
    { day: "S", min: 11 },
  ],
  chaptersInProgress: 7,
  chaptersCompleted: 3,
  bestQuiz: 92,
  bestQuizDelta: 7,
  aiQuestionsToday: 4,
  aiQuotaDaily: 50,
  continue: {
    classLabel: "Class 11 · Physics",
    chapter: "Units and Measurements",
    mode: "Video overview",
    progress: "4 min 32 sec left",
    progressPct: 67,
  },
  recent: [
    { subject: "Physics",     chapter: "Motion in a Straight Line",  pct: 45, when: "Yesterday" },
    { subject: "Chemistry",   chapter: "Some Basic Concepts",        pct: 80, when: "2 days ago" },
    { subject: "Mathematics", chapter: "Sets",                       pct: 32, when: "3 days ago" },
    { subject: "Biology",     chapter: "The Living World",           pct: 12, when: "5 days ago" },
  ],
  subjects: [
    { name: "Physics",     pct: 25, chapters: 16, num: "01" },
    { name: "Chemistry",   pct: 18, chapters: 14, num: "02" },
    { name: "Mathematics", pct: 42, chapters: 16, num: "03" },
    { name: "Biology",     pct:  6, chapters: 22, num: "04" },
  ],
  quizTrend: [
    { n: 1, score: 60 }, { n: 2, score: 72 }, { n: 3, score: 68 },
    { n: 4, score: 85 }, { n: 5, score: 92 },
  ],
  chapterList: [
    { n: 1, title: "Units and Measurements",          status: "in-progress", pct: 67 },
    { n: 2, title: "Motion in a Straight Line",       status: "free",        pct:  0 },
    { n: 3, title: "Motion in a Plane",               status: "locked" },
    { n: 4, title: "Laws of Motion",                  status: "locked" },
    { n: 5, title: "Work, Energy and Power",          status: "locked" },
    { n: 6, title: "System of Particles and Rotational Motion", status: "locked" },
    { n: 7, title: "Gravitation",                     status: "locked" },
    { n: 8, title: "Mechanical Properties of Solids", status: "locked" },
  ],
};

const STUDY_MODES: { id: string; n: string; label: string; hint: string; icon: LucideIcon; tint: string }[] = [
  { id: "tutor",    n: "01", label: "AI Tutor",     hint: "Ask, grounded in this chapter.", icon: Sparkles,    tint: "violet" },
  { id: "podcast",  n: "02", label: "Audio lesson", hint: "EN / HI / KN where available.",  icon: Podcast,     tint: "rose" },
  { id: "video",    n: "03", label: "Video",        hint: "Guided walkthrough.",            icon: Video,       tint: "red" },
  { id: "textbook", n: "04", label: "Textbook",     hint: "Searchable, annotatable PDF.",   icon: BookOpen,    tint: "blue" },
  { id: "flash",    n: "05", label: "Flashcards",   hint: "Quick recall with KaTeX.",       icon: Layers,      tint: "amber" },
  { id: "mind",     n: "06", label: "Mind map",     hint: "How concepts connect.",          icon: Network,     tint: "teal" },
  { id: "quiz",     n: "07", label: "Quiz",         hint: "MCQs with explanations.",        icon: HelpCircle,  tint: "purple" },
  { id: "qbank",    n: "08", label: "Question bank",hint: "Practice + PYQs.",               icon: CheckCircle2,tint: "orange" },
  { id: "slides",   n: "09", label: "Slides",       hint: "Five-minute chapter skim.",      icon: Presentation,tint: "indigo" },
  { id: "notes",    n: "10", label: "Notes",        hint: "Exam-focused revision notes.",   icon: FileText,    tint: "emerald" },
];

// ===========================================================================
// Page
// ===========================================================================
export function StyleguidePage() {
  const [view, setView] = useState<View>("dashboard");
  const [mode, setMode] = useState<"both" | "light" | "dark">("both");

  return (
    <div className="min-h-screen bg-bg text-text font-sans">
      <header className="sticky top-0 z-30 border-b border-border bg-bg/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-14 flex items-center justify-between gap-4">
          <Link to="/subjects" className="text-sm text-text-muted hover:text-text transition-colors">
            ← Back to app
          </Link>
          <div className="hidden md:block text-xs uppercase tracking-[0.18em] text-text-muted">
            Portal preview — 4 directions, 2 modes
          </div>
          <div className="flex items-center gap-4">
            <SegmentedControl
              label="View"
              value={view}
              setValue={setView}
              options={[
                ["dashboard",  "Dashboard"],
                ["chapters",   "Chapters"],
                ["studymodes", "Study modes"],
              ]}
            />
            <div className="hidden sm:block w-px h-5 bg-border" />
            <SegmentedControl
              label="Mode"
              value={mode}
              setValue={setMode}
              options={[
                ["both",  "Both"],
                ["light", "Light"],
                ["dark",  "Dark"],
              ]}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10 lg:py-14">
        <section className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-text-subtle mb-3">Brief</p>
          <h1
            className="font-medium tracking-tight leading-[1.06]"
            style={{ fontFamily: "'Geist', sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
          >
            Same{" "}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>
              app
            </span>
            , nine palettes.
          </h1>
          <p className="mt-4 text-text-muted leading-relaxed">
            Every panel renders the same portal screen with identical sample data — only the
            palette and design language change. The first group is the student-friendly SaaS
            direction (rounded cards, soft hover tints) in five different palettes. The second
            group is the editorial direction (hairline borders, serif italics on emphasis words)
            kept for comparison. Toggle View at the top to switch between Dashboard, Chapters,
            and Study modes.
          </p>
        </section>

        {/* SaaS-style themes ------------------------------------------------ */}
        <section className="mb-6">
          <div className="flex items-baseline gap-3 border-b border-border pb-3 mb-8">
            <span className="text-xs uppercase tracking-[0.18em] text-text-subtle">Group 1</span>
            <h2 className="text-lg font-semibold tracking-tight">
              SaaS direction — same layout as current v2, different palettes
            </h2>
          </div>
          <div className="space-y-16">
            {SAAS_THEMES.map((theme) => (
              <ThemeBlock key={theme.id} theme={theme} view={view} mode={mode} />
            ))}
          </div>
        </section>

        {/* Editorial-style themes ------------------------------------------ */}
        <section className="mt-20">
          <div className="flex items-baseline gap-3 border-b border-border pb-3 mb-8">
            <span className="text-xs uppercase tracking-[0.18em] text-text-subtle">Group 2</span>
            <h2 className="text-lg font-semibold tracking-tight">
              Editorial direction — kept for reference
            </h2>
          </div>
          <p className="text-sm text-text-muted mb-8 max-w-2xl">
            Different layout language entirely: hairline borders instead of card surfaces,
            serif italics on emphasis words, numbered nav items. You said this didn't feel
            quite right for the student audience — left visible here so you can re-check
            the contrast against the SaaS group above.
          </p>
          <div className="space-y-16">
            {EDITORIAL_THEMES.map((theme) => (
              <ThemeBlock key={theme.id} theme={theme} view={view} mode={mode} />
            ))}
          </div>
        </section>

        <section className="mt-24 pt-12 border-t border-border">
          <p className="text-xs uppercase tracking-[0.18em] text-text-subtle mb-3">Pick one</p>
          <p
            className="font-medium tracking-tight"
            style={{ fontFamily: "'Geist', sans-serif", fontSize: "clamp(1.25rem, 2.5vw, 1.75rem)" }}
          >
            Tell me{" "}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>
              Indigo, Cobalt, Emerald, Iris, Coral, or Pulse
            </span>
            {" "}— or one of the editorial picks — and I'll roll the chosen direction across every page of the v2 app.
          </p>
          <p className="mt-3 text-sm text-text-muted">
            Mix-and-match is still on the table (e.g., Cobalt palette but with Iris's dark-mode treatment).
          </p>
        </section>
      </main>
    </div>
  );
}

function SegmentedControl<T extends string>({
  label,
  value,
  setValue,
  options,
}: {
  label: string;
  value: T;
  setValue: (v: T) => void;
  options: [T, string][];
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="hidden md:inline text-[10px] uppercase tracking-[0.16em] text-text-subtle">{label}</span>
      <div className="flex items-center gap-0.5 p-0.5 rounded-md border border-border bg-surface">
        {options.map(([v, l]) => (
          <button
            key={v}
            onClick={() => setValue(v)}
            className={cn(
              "px-2.5 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap",
              value === v ? "bg-surface-2 text-text" : "text-text-muted hover:text-text",
            )}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ThemeBlock: header + 1 or 2 panels per theme based on the mode toggle.
// ---------------------------------------------------------------------------
function ThemeBlock({
  theme,
  view,
  mode,
}: {
  theme: StyleTheme;
  view: View;
  mode: "both" | "light" | "dark";
}) {
  return (
    <section>
      <div className="mb-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2
            className="font-medium tracking-tight"
            style={{ fontFamily: "'Geist', sans-serif", fontSize: "1.75rem", letterSpacing: "-0.02em" }}
          >
            {theme.name}
          </h2>
          <span className="text-sm text-text-muted">— {theme.tagline}</span>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-text-muted leading-relaxed">{theme.description}</p>
      </div>

      <div className="space-y-5">
        {(mode === "both" || mode === "light") && (
          <PortalPanel theme={theme} themeMode="light" view={view} />
        )}
        {(mode === "both" || mode === "dark") && (
          <PortalPanel theme={theme} themeMode="dark" view={view} />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// PortalPanel: scoped CSS variables + mini app chrome + selected view content.
// Forks on theme.id to render the SaaS-style chrome (Indigo) versus the
// editorial chrome (the other three).
// ---------------------------------------------------------------------------
function PortalPanel({
  theme,
  themeMode,
  view,
}: {
  theme: StyleTheme;
  themeMode: ThemeMode;
  view: View;
}) {
  const tokens = themeMode === "light" ? theme.light : theme.dark;
  const style = tokenToCssVars(tokens) as CSSProperties;
  const useEditorial = isEditorialTheme(theme);

  return (
    <div
      style={style}
      className="rounded-xl overflow-hidden border border-border"
    >
      <div
        className="px-5 py-2 border-b border-border bg-surface-2 text-[11px] uppercase tracking-[0.16em] flex items-center justify-between"
        style={{ color: "rgb(var(--text-muted))" }}
      >
        <span>{theme.name} · {themeMode}</span>
        <span className="hidden sm:inline">{theme.accentNote}</span>
      </div>
      <div style={{ background: "rgb(var(--bg))", color: "rgb(var(--text))" }}>
        {useEditorial ? <EditorialPortal view={view} /> : <IndigoPortal view={view} />}
      </div>
    </div>
  );
}

// ===========================================================================
// INDIGO PORTAL — the current SaaS-style v2 design (rounded cards, brand-soft
// hovers, lifted shadows). Uses Inter / Plus Jakarta Sans for fonts.
// ===========================================================================
function IndigoPortal({ view }: { view: View }) {
  return (
    <div className="flex" style={{ fontFamily: "'Inter Variable', 'Inter', system-ui, sans-serif" }}>
      <IndigoSidebar active={view} />
      <div className="flex-1 min-w-0">
        <IndigoTopbar />
        <div className="p-6 lg:p-8" style={{ minHeight: 600 }}>
          {view === "dashboard"  && <IndigoDashboard />}
          {view === "chapters"   && <IndigoChapters />}
          {view === "studymodes" && <IndigoStudyModes />}
        </div>
      </div>
    </div>
  );
}

function IndigoSidebar({ active }: { active: View }) {
  const items: { id: string; label: string; icon: LucideIcon }[] = [
    { id: "dashboard",  label: "Dashboard",  icon: Home },
    { id: "chapters",   label: "Subjects",   icon: BookOpen },
    { id: "studymodes", label: "Study",      icon: Sparkles },
    { id: "chat",       label: "AI Tutor",   icon: MessageCircle },
    { id: "billing",    label: "Billing",    icon: CreditCard },
    { id: "settings",   label: "Settings",   icon: Settings },
  ];
  const activeMatch = active === "chapters" ? "chapters" : active === "studymodes" ? "studymodes" : "dashboard";
  return (
    <aside
      className="hidden lg:flex flex-col w-52 border-r"
      style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))" }}
    >
      <div className="p-4 flex items-center gap-2 border-b" style={{ borderColor: "rgb(var(--border))" }}>
        <span
          className="grid h-7 w-7 place-items-center rounded-md text-white font-bold text-sm"
          style={{ background: "rgb(var(--brand))" }}
        >
          S
        </span>
        <span className="font-semibold tracking-tight">StaxED</span>
      </div>
      <nav className="flex flex-col gap-1 p-3 flex-1">
        {items.map(({ id, label, icon: Icon }) => {
          const isActive =
            (id === "dashboard" && activeMatch === "dashboard") ||
            (id === "chapters" && activeMatch === "chapters") ||
            (id === "studymodes" && activeMatch === "studymodes");
          return (
            <div
              key={id}
              className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg"
              style={{
                background: isActive ? "rgb(var(--brand-soft))" : "transparent",
                color: isActive ? "rgb(var(--brand))" : "rgb(var(--text-muted))",
              }}
            >
              <Icon className="h-4 w-4" />
              {label}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function IndigoTopbar() {
  return (
    <div
      className="h-14 flex items-center justify-between px-6 border-b"
      style={{ background: "rgba(var(--bg) / 0.85)", borderColor: "rgb(var(--border))", backdropFilter: "blur(8px)" }}
    >
      <span className="text-sm text-text-muted">{SAMPLE.user.name.toLowerCase()}@staxed.in</span>
      <div
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
        style={{ background: "rgb(var(--accent-soft))", color: "rgb(var(--accent))" }}
      >
        <Flame className="h-3.5 w-3.5" />
        {SAMPLE.streak} day streak
      </div>
    </div>
  );
}

function IndigoDashboard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {SAMPLE.date.greeting}, {SAMPLE.user.firstName}.
        </h1>
        <p className="mt-1 text-sm text-text-muted">{SAMPLE.date.display}</p>
      </div>

      {/* Continue learning */}
      <div
        className="rounded-xl border p-5 flex items-center gap-5 flex-wrap"
        style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))", boxShadow: "0 1px 2px rgb(15 23 42 / 0.04)" }}
      >
        <div
          className="grid place-items-center h-14 w-14 rounded-xl"
          style={{ background: "rgb(var(--brand-soft))", color: "rgb(var(--brand))" }}
        >
          <Play className="h-6 w-6 fill-current" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-medium" style={{ color: "rgb(var(--brand))" }}>{SAMPLE.continue.classLabel}</div>
          <div className="mt-0.5 text-lg font-semibold">{SAMPLE.continue.chapter}</div>
          <div className="mt-1 text-sm text-text-muted">{SAMPLE.continue.mode} · {SAMPLE.continue.progress}</div>
        </div>
        <button
          className="px-4 py-2 rounded-lg text-sm font-medium text-white whitespace-nowrap"
          style={{ background: "rgb(var(--brand))" }}
        >
          Continue
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <IndigoStatCard title="This week" big={formatMin(SAMPLE.thisWeekMinutes)} sub="study time" sparkline />
        <IndigoStatCard title="In progress" big={`${SAMPLE.chaptersInProgress}`} sub="chapters" />
        <IndigoStatCard title="Best quiz" big={`${SAMPLE.bestQuiz}%`} sub="last 30 days" trend={`+${SAMPLE.bestQuizDelta}`} />
      </div>

      {/* Recently studied + subject progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm font-semibold mb-3">Recently studied</h2>
          <div className="space-y-2">
            {SAMPLE.recent.slice(0, 3).map((r) => (
              <div
                key={r.chapter}
                className="rounded-lg border p-3.5 flex items-center gap-3"
                style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium" style={{ color: "rgb(var(--brand))" }}>{r.subject}</div>
                  <div className="text-sm font-medium truncate">{r.chapter}</div>
                  <div className="mt-0.5 text-xs text-text-muted">{r.when}</div>
                </div>
                <div className="text-sm font-semibold tabular-nums">{r.pct}%</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold mb-3">Subject progress</h2>
          <div className="space-y-3">
            {SAMPLE.subjects.map((s) => (
              <div key={s.name}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium">{s.name}</span>
                  <span className="tabular-nums text-text-muted">{s.pct}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
                  <div className="h-full rounded-full" style={{ background: "rgb(var(--brand))", width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quiz trend */}
      <div
        className="rounded-xl border p-5"
        style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))", boxShadow: "0 1px 2px rgb(15 23 42 / 0.04)" }}
      >
        <h2 className="text-sm font-semibold mb-2">Quiz performance — last 5</h2>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={SAMPLE.quizTrend} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
              <XAxis dataKey="n" stroke="rgb(var(--text-subtle))" fontSize={11} />
              <YAxis stroke="rgb(var(--text-subtle))" fontSize={11} domain={[0, 100]} tickCount={3} />
              <Tooltip
                contentStyle={{
                  background: "rgb(var(--surface))",
                  border: "1px solid rgb(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "rgb(var(--text))",
                }}
              />
              <Line type="monotone" dataKey="score" stroke="rgb(var(--brand))" strokeWidth={2} dot={{ r: 3, fill: "rgb(var(--brand))" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function IndigoStatCard({
  title, big, sub, sparkline, trend,
}: { title: string; big: string; sub: string; sparkline?: boolean; trend?: string }) {
  return (
    <div
      className="rounded-xl border p-5"
      style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))", boxShadow: "0 1px 2px rgb(15 23 42 / 0.04)" }}
    >
      <div className="text-xs font-medium text-text-muted">{title}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-2xl font-bold tabular-nums" style={{ color: "rgb(var(--text))" }}>{big}</div>
        {trend ? (
          <span className="text-xs font-medium" style={{ color: "rgb(var(--success))" }}>
            {trend}
          </span>
        ) : null}
      </div>
      <div className="text-xs text-text-muted mt-0.5">{sub}</div>
      {sparkline ? (
        <div className="mt-3 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={SAMPLE.thisWeekSparkline}>
              <Area
                type="monotone"
                dataKey="min"
                stroke="rgb(var(--brand))"
                strokeWidth={1.5}
                fill="rgb(var(--brand) / 0.12)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}

function IndigoChapters() {
  return (
    <div>
      <p className="text-xs text-text-muted">Subjects · Physics</p>
      <h1 className="mt-2 text-2xl font-bold tracking-tight">Physics</h1>
      <p className="mt-1 text-sm text-text-muted">{SAMPLE.subjects[0].chapters} chapters · {SAMPLE.subjects[0].pct}% complete</p>

      <div
        className="mt-6 rounded-xl border divide-y overflow-hidden"
        style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))" }}
      >
        {SAMPLE.chapterList.map((c) => (
          <div key={c.n} className="flex items-center gap-3 px-4 py-3.5">
            <span
              className="grid place-items-center h-8 w-8 rounded-md text-xs font-semibold tabular-nums"
              style={{ background: "rgb(var(--surface-2))", color: "rgb(var(--text-muted))" }}
            >
              {c.n}
            </span>
            <span className="flex-1 text-sm font-medium truncate">{c.title}</span>
            {c.status === "free" ? (
              <span
                className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ background: "rgb(var(--brand-soft))", color: "rgb(var(--brand))" }}
              >
                Free
              </span>
            ) : c.status === "in-progress" ? (
              <span className="text-xs font-medium tabular-nums" style={{ color: "rgb(var(--brand))" }}>
                {c.pct}%
              </span>
            ) : (
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ background: "rgb(var(--surface-2))", color: "rgb(var(--text-muted))" }}
              >
                <Lock className="h-3 w-3" />
                Locked
              </span>
            )}
            <ChevronRight className="h-4 w-4 text-text-subtle" />
          </div>
        ))}
      </div>
    </div>
  );
}

function IndigoStudyModes() {
  return (
    <div>
      <p className="text-xs text-text-muted">Subjects · Physics · Chapter 1</p>
      <h1 className="mt-2 text-2xl font-bold tracking-tight">{SAMPLE.continue.chapter}</h1>
      <p className="mt-1 text-sm text-text-muted">Switch between modes any time — your progress stays in sync.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {STUDY_MODES.map((m) => (
          <div
            key={m.id}
            className="rounded-xl border p-4"
            style={{ background: "rgb(var(--surface))", borderColor: "rgb(var(--border))" }}
          >
            <div
              className="grid place-items-center h-9 w-9 rounded-lg"
              style={{
                background: `rgb(var(--brand-soft))`,
                color: `rgb(var(--brand))`,
              }}
            >
              <m.icon className="h-4 w-4" />
            </div>
            <div className="mt-3 text-sm font-semibold">{m.label}</div>
            <div className="mt-0.5 text-xs text-text-muted leading-relaxed">{m.hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// EDITORIAL PORTAL — the new direction. Hairline borders, no shadows, serif
// emphasis on key words, numbered nav items, accent used sparingly.
// Used for Terracotta / Moss / Midnight.
// ===========================================================================
function EditorialPortal({ view }: { view: View }) {
  return (
    <div
      className="flex"
      style={{ fontFamily: "'Geist', system-ui, sans-serif" }}
    >
      <EditorialSidebar active={view} />
      <div className="flex-1 min-w-0">
        <EditorialTopbar />
        <div className="p-6 lg:p-10" style={{ minHeight: 600 }}>
          {view === "dashboard"  && <EditorialDashboard />}
          {view === "chapters"   && <EditorialChapters />}
          {view === "studymodes" && <EditorialStudyModes />}
        </div>
      </div>
    </div>
  );
}

function EditorialSidebar({ active }: { active: View }) {
  const items: { n: string; id: string; label: string }[] = [
    { n: "01", id: "dashboard",  label: "Home" },
    { n: "02", id: "chapters",   label: "Subjects" },
    { n: "03", id: "studymodes", label: "Study" },
    { n: "04", id: "chat",       label: "Tutor" },
    { n: "05", id: "billing",    label: "Billing" },
    { n: "06", id: "settings",   label: "Settings" },
  ];
  return (
    <aside
      className="hidden lg:flex flex-col w-52 border-r"
      style={{ borderColor: "rgb(var(--border))" }}
    >
      <div className="p-5 border-b" style={{ borderColor: "rgb(var(--border))" }}>
        <div className="font-medium tracking-tight" style={{ fontFamily: "'Geist', sans-serif", fontSize: "1.05rem" }}>
          StaxED<span style={{ color: "rgb(var(--brand))" }}>.</span>
        </div>
        <div className="mt-0.5 text-[11px] uppercase tracking-[0.16em]" style={{ color: "rgb(var(--text-subtle))" }}>
          Class 11
        </div>
      </div>
      <nav className="flex flex-col py-2 flex-1">
        {items.map((it) => {
          const isActive =
            (it.id === "dashboard"  && active === "dashboard")  ||
            (it.id === "chapters"   && active === "chapters")   ||
            (it.id === "studymodes" && active === "studymodes");
          return (
            <div
              key={it.id}
              className="flex items-center gap-3 px-5 py-2 text-sm transition-colors"
              style={{
                color: isActive ? "rgb(var(--text))" : "rgb(var(--text-muted))",
                borderLeft: isActive ? "2px solid rgb(var(--brand))" : "2px solid transparent",
                marginLeft: -1,
              }}
            >
              <span className="font-mono text-[10px] tabular-nums" style={{ color: "rgb(var(--text-subtle))" }}>
                {it.n}
              </span>
              <span className={cn("font-medium", isActive && "")}>
                {it.label}
              </span>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function EditorialTopbar() {
  return (
    <div
      className="h-12 flex items-center justify-between px-6 border-b"
      style={{ borderColor: "rgb(var(--border))" }}
    >
      <span className="text-xs tracking-[0.12em] uppercase" style={{ color: "rgb(var(--text-muted))" }}>
        {SAMPLE.user.name.toLowerCase()}@staxed.in
      </span>
      <span className="flex items-center gap-2 text-xs" style={{ color: "rgb(var(--text-muted))" }}>
        <span className="font-mono tabular-nums" style={{ color: "rgb(var(--brand))" }}>{SAMPLE.streak}</span>
        <span className="uppercase tracking-[0.14em] text-[10px]">day streak</span>
      </span>
    </div>
  );
}

function EditorialDashboard() {
  return (
    <div className="space-y-12">
      {/* Greeting + continue */}
      <section>
        <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "rgb(var(--text-subtle))" }}>
          {SAMPLE.date.display}
        </p>
        <h1
          className="mt-4 font-medium tracking-tight"
          style={{
            fontFamily: "'Geist', sans-serif",
            fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
            letterSpacing: "-0.025em",
            lineHeight: 1.08,
          }}
        >
          {SAMPLE.date.greeting},{" "}
          <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>
            {SAMPLE.user.firstName}
          </span>
          .
        </h1>

        <div className="mt-8 border-y py-6" style={{ borderColor: "rgb(var(--border))" }}>
          <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "rgb(var(--text-subtle))" }}>
            Continue · {SAMPLE.continue.classLabel}
          </p>
          <h2
            className="mt-3 font-medium tracking-tight"
            style={{ fontFamily: "'Geist', sans-serif", fontSize: "clamp(1.5rem, 3vw, 2.25rem)", letterSpacing: "-0.02em" }}
          >
            {SAMPLE.continue.chapter}
          </h2>
          <div className="mt-3 flex items-center gap-4 text-sm" style={{ color: "rgb(var(--text-muted))" }}>
            <span>
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", color: "rgb(var(--brand))" }}>
                {SAMPLE.continue.mode}
              </span>
              {" "}· {SAMPLE.continue.progress}
            </span>
            <span className="font-mono tabular-nums">{SAMPLE.continue.progressPct}%</span>
          </div>
          <div className="mt-5 flex items-center gap-5">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors"
              style={{ background: "rgb(var(--text))", color: "rgb(var(--bg))" }}
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
            <a
              className="link-underline text-sm font-medium"
              style={{ color: "rgb(var(--text))" }}
              onClick={(e) => e.preventDefault()}
              href="#"
            >
              Pick a different chapter →
            </a>
          </div>
        </div>
      </section>

      {/* Stats — editorial table */}
      <section>
        <p className="text-xs uppercase tracking-[0.18em] mb-5" style={{ color: "rgb(var(--text-subtle))" }}>
          This week
        </p>
        <div className="grid grid-cols-3 gap-px" style={{ background: "rgb(var(--border))" }}>
          <EditorialStat title="Time studied" big={formatMin(SAMPLE.thisWeekMinutes)} sparkline />
          <EditorialStat title="In progress" big={`${SAMPLE.chaptersInProgress}`} sub={`${SAMPLE.chaptersCompleted} completed`} />
          <EditorialStat title="Best quiz" big={`${SAMPLE.bestQuiz}%`} sub={`+${SAMPLE.bestQuizDelta} on prior`} />
        </div>
      </section>

      {/* Recent + subject progress */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "rgb(var(--text-subtle))" }}>
            Recently studied
          </p>
          <ul className="border-y" style={{ borderColor: "rgb(var(--border))" }}>
            {SAMPLE.recent.map((r, i) => (
              <li
                key={r.chapter}
                className={cn("py-3.5 flex items-center gap-4", i > 0 && "border-t")}
                style={{ borderColor: "rgb(var(--border))" }}
              >
                <span className="font-mono text-xs tabular-nums w-8" style={{ color: "rgb(var(--text-subtle))" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "rgb(var(--text-muted))" }}>
                    {r.subject}
                  </div>
                  <div className="text-sm font-medium truncate">{r.chapter}</div>
                </div>
                <span className="font-mono text-xs tabular-nums w-10 text-right" style={{ color: "rgb(var(--text))" }}>
                  {r.pct}%
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "rgb(var(--text-subtle))" }}>
            Subject progress
          </p>
          <div className="space-y-5">
            {SAMPLE.subjects.map((s) => (
              <div key={s.name}>
                <div className="flex items-baseline justify-between text-sm mb-2">
                  <span className="font-medium">{s.name}</span>
                  <span className="font-mono tabular-nums text-xs" style={{ color: "rgb(var(--text-muted))" }}>
                    {s.pct}%
                  </span>
                </div>
                <div className="h-px w-full relative" style={{ background: "rgb(var(--surface-3))" }}>
                  <div className="absolute top-0 left-0 h-px" style={{ background: "rgb(var(--brand))", width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quiz trend */}
      <section>
        <p className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: "rgb(var(--text-subtle))" }}>
          Quiz performance — last 5
        </p>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={SAMPLE.quizTrend} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
              <XAxis dataKey="n" stroke="rgb(var(--text-subtle))" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="rgb(var(--text-subtle))" fontSize={10} domain={[0, 100]} tickCount={3} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  background: "rgb(var(--surface))",
                  border: "1px solid rgb(var(--border))",
                  borderRadius: 4,
                  fontSize: 11,
                  color: "rgb(var(--text))",
                }}
              />
              <Line type="monotone" dataKey="score" stroke="rgb(var(--brand))" strokeWidth={1.5} dot={{ r: 2.5, fill: "rgb(var(--brand))" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}

function EditorialStat({
  title, big, sub, sparkline,
}: { title: string; big: string; sub?: string; sparkline?: boolean }) {
  return (
    <div className="p-6" style={{ background: "rgb(var(--bg))" }}>
      <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "rgb(var(--text-subtle))" }}>
        {title}
      </div>
      <div
        className="mt-3 font-medium tabular-nums"
        style={{ fontFamily: "'Geist', sans-serif", fontSize: "1.875rem", letterSpacing: "-0.02em" }}
      >
        {big}
      </div>
      {sub ? <div className="mt-0.5 text-xs" style={{ color: "rgb(var(--text-muted))" }}>{sub}</div> : null}
      {sparkline ? (
        <div className="mt-3 h-8">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={SAMPLE.thisWeekSparkline}>
              <Area
                type="monotone"
                dataKey="min"
                stroke="rgb(var(--brand))"
                strokeWidth={1}
                fill="rgb(var(--brand) / 0.08)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}

function EditorialChapters() {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "rgb(var(--text-subtle))" }}>
        Subjects · Physics
      </p>
      <h1
        className="mt-3 font-medium tracking-tight"
        style={{ fontFamily: "'Geist', sans-serif", fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", letterSpacing: "-0.025em" }}
      >
        Physics
      </h1>
      <p className="mt-2 text-sm" style={{ color: "rgb(var(--text-muted))" }}>
        {SAMPLE.subjects[0].chapters} chapters ·{" "}
        <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", color: "rgb(var(--brand))" }}>
          {SAMPLE.subjects[0].pct}%
        </span>{" "}
        complete
      </p>

      <ul className="mt-8 border-y" style={{ borderColor: "rgb(var(--border))" }}>
        {SAMPLE.chapterList.map((c, i) => (
          <li
            key={c.n}
            className={cn("py-3.5 px-1 flex items-center gap-4", i > 0 && "border-t")}
            style={{ borderColor: "rgb(var(--border))" }}
          >
            <span className="font-mono text-xs tabular-nums w-8" style={{ color: "rgb(var(--text-subtle))" }}>
              {String(c.n).padStart(2, "0")}
            </span>
            <span className="flex-1 text-sm font-medium">{c.title}</span>
            {c.status === "free" ? (
              <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgb(var(--brand))" }}>
                Free
              </span>
            ) : c.status === "in-progress" ? (
              <span className="font-mono tabular-nums text-xs" style={{ color: "rgb(var(--brand))" }}>{c.pct}%</span>
            ) : (
              <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgb(var(--text-subtle))" }}>
                Locked
              </span>
            )}
            <ChevronRight className="h-4 w-4" style={{ color: "rgb(var(--text-subtle))" }} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function EditorialStudyModes() {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "rgb(var(--text-subtle))" }}>
        Subjects · Physics · Chapter 1
      </p>
      <h1
        className="mt-3 font-medium tracking-tight max-w-3xl"
        style={{ fontFamily: "'Geist', sans-serif", fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", letterSpacing: "-0.025em" }}
      >
        {SAMPLE.continue.chapter}
      </h1>
      <p className="mt-3 text-sm" style={{ color: "rgb(var(--text-muted))" }}>
        Switch{" "}
        <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", color: "rgb(var(--brand))" }}>
          modes
        </span>
        {" "}without losing your place.
      </p>

      <div
        className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px"
        style={{ background: "rgb(var(--border))" }}
      >
        {STUDY_MODES.map((m) => (
          <div
            key={m.id}
            className="p-5"
            style={{ background: "rgb(var(--bg))" }}
          >
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] tabular-nums" style={{ color: "rgb(var(--text-subtle))" }}>
                {m.n}
              </span>
              <m.icon className="h-4 w-4" style={{ color: "rgb(var(--text-subtle))" }} />
            </div>
            <div
              className="mt-4 font-medium"
              style={{ fontFamily: "'Geist', sans-serif", fontSize: "1.0625rem", letterSpacing: "-0.01em" }}
            >
              {m.label}
            </div>
            <div className="mt-1.5 text-xs leading-relaxed" style={{ color: "rgb(var(--text-muted))" }}>
              {m.hint}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================
function formatMin(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}
